// screens/AppendiceTheoryScreen.tsx — R2-2: the Appendice TEORIA view.
// Route: /appendice/:argomentoId/teoria
//
// The full per-argomento theory chapter — the missing FE half of P3. The backend
// has served `GET /api/argomenti/:id/chapter` (frozen theory asset merged with
// LIVE DB principi) since commit e30e97a, but no screen consumed it, so the
// Appendice showed only the per-principle enunciato + trappole. This screen
// renders the complete chapter theory faithfully.
//
// IA (spec p3-appendice-spec.md §6 Decision 4 — "theory-first chapter"):
//   1. "In breve" (the founder-reviewed prof_layer takeaways).
//   2. Tiered theory — `core` sub-sections expanded; each shows its frozen
//      paragraphs + ✅ VERO ("letture_confermate") and ❌ FALSO
//      ("letture_da_scartare") quiz readings (spec §0.2 H3 — the two never mix,
//      and a da_scartare item is NEVER shown as an affirmative statement).
//   3. "Le regole chiave" — the principi (key_rules) as links into the
//      per-principle detail (/appendice/:argomentoId/:principleId).
//   4. Collapsed "Approfondimenti" tail (`approfondimenti`-tier sub-sections).
//
// State-free (spec §4): reads only the chapter endpoint — NO mastery / Indice /
// per-student state. No mastery colors here (the Appendice is the one neutral
// student surface). Light-only tokens, mobile-first (no horizontal overflow).

import type { JSX, ReactNode } from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { useShellNav } from '../components/AppFrame';
import { ProfProse } from '../components/ProfProse';
import { SelectProfFigureButton } from '../components/SelectProfFigureButton';
import { useTokens } from '../state/theme';
import { anchorForSubsection } from '../utils/appendice-search';
import { appendiceApi, chapterContenutoToText as contenutoToText } from '../api/appendice';
import { argomentoHue } from '../state/appendice-identity';
import type { AppendiceChapter, ChapterSubSection, ChapterKeyRule, ChapterSubSectionImage } from '../api/appendice';
import { lessonAssetSrc } from '../state/canonical-learning';
import { kbApi, type ArgomentoDto } from '../api/kb';
import { READING_DOCK_H } from '../tokens';

// V29 S4 — module cache for the argomenti list (sibling prev/next nav). One
// fetch per session; the index screen has its own copy of this data.
let argomentiCache: Promise<ArgomentoDto[]> | null = null;
function listArgomentiCached(): Promise<ArgomentoDto[]> {
  if (!argomentiCache) {
    argomentiCache = kbApi.listArgomenti().catch((err) => {
      argomentiCache = null;
      throw err;
    });
  }
  return argomentiCache;
}

export default function AppendiceTheoryScreen(): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const { argomentoId } = useParams<{ argomentoId: string }>();
  // Restyle (2026-07-14): the linear reader inherits the argomento's hue.
  const hue = argomentoHue(argomentoId);
  // FP6 deep-link: a Search-v2 sub-section hit lands here with `?sub=<num>`; the
  // matching SubSectionCard auto-expands, scrolls into view, and gets an accent
  // border. Scoped to this teoria route — does NOT touch the L1/L2 history-aware
  // back contract.
  const [searchParams] = useSearchParams();
  const targetSub = searchParams.get('sub');
  const urlAnchor = targetSub ? anchorForSubsection(targetSub) : null;
  // V29 S4: the chapter-index dropdown jumps by setting a local target — same
  // open+scroll mechanism as the ?sub deep-link.
  const [jumpAnchor, setJumpAnchor] = useState<string | null>(null);
  const targetAnchor = jumpAnchor ?? urlAnchor;
  const [indexOpen, setIndexOpen] = useState(false);

  const { goBack } = useShellNav();
  const [chapter, setChapter] = useState<AppendiceChapter | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sibling argomenti for the Precedente/Successivo nav (founder decision:
  // prev/next with NAMES, at the top).
  const [siblings, setSiblings] = useState<{ prev: ArgomentoDto | null; next: ArgomentoDto | null }>({ prev: null, next: null });
  // Reading progress (0..1) of the scroll region — drives the thin bar.
  const [readPct, setReadPct] = useState(0);
  const mainRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!argomentoId) return;
    setLoading(true);
    setError(null);
    appendiceApi
      .getChapterCached(argomentoId)
      .then((data) => setChapter(data))
      .catch(() => setError('Non sono riuscito a caricare la teoria di questo argomento. Riprova tra qualche secondo.'))
      .finally(() => setLoading(false));
  }, [argomentoId]);

  useEffect(() => {
    if (!argomentoId) return;
    let alive = true;
    void listArgomentiCached()
      .then((all) => {
        if (!alive) return;
        const sorted = [...all].sort((a, b) => a.sort_order - b.sort_order);
        const idx = sorted.findIndex((a) => a.id === argomentoId);
        setSiblings({
          prev: idx > 0 ? sorted[idx - 1] : null,
          next: idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null,
        });
      })
      .catch(() => { /* sibling nav silently absent on fetch failure */ });
    return () => { alive = false; };
  }, [argomentoId]);

  const handleScroll = useCallback(() => {
    const el = mainRef.current;
    if (!el) return;
    const total = el.scrollHeight - el.clientHeight;
    setReadPct(total > 0 ? Math.min(1, Math.max(0, el.scrollTop / total)) : 0);
  }, []);

  const jumpToSection = useCallback((num: string) => {
    const anchor = anchorForSubsection(num);
    setJumpAnchor(anchor);
    setIndexOpen(false);
    const el = document.getElementById(anchor);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, []);

  const coreSections = (chapter?.sub_sections ?? []).filter((s) => s.tier === 'core');
  const deepSections = (chapter?.sub_sections ?? []).filter((s) => s.tier !== 'core');
  const allSections = [...coreSections, ...deepSections];
  const punti = chapter?.prof_layer?.punti_chiave ?? [];
  const keyRules = chapter?.key_rules ?? [];

  const heading = chapter?.sezione_title ?? (argomentoId ? `Argomento ${argomentoId}` : 'Teoria');
  const breadcrumb = chapter
    ? `${chapter.argomento_label} · Teoria`
    : 'Teoria';

  return (
    <Screen>
      <TopChrome
        title={heading}
        subtitle={breadcrumb}
        // Hierarchical back (2026-07-14 r4): one level up, deterministic.
        onBack={argomentoId ? () => navigate(`/appendice/${argomentoId}`, { replace: true }) : goBack}
      />
      {/* V29 S4 — reading progress: a thin gradient bar under the chrome. */}
      <div aria-hidden="true" style={{ height: 3, background: tokens.line, flexShrink: 0 }}>
        <div
          data-testid="teoria-read-progress"
          style={{
            height: '100%',
            width: `${Math.round(readPct * 100)}%`,
            background: hue.grad,
            transition: 'width 0.12s linear',
          }}
        />
      </div>

      {/* V29 S4 — chapter-index dropdown (the 62-subsection arg8 case): a
          collapsible list of every sub-section; tap → expand + scroll. */}
      {indexOpen && allSections.length > 0 && (
        <div
          data-testid="teoria-index-panel"
          role="dialog"
          aria-label="Indice del capitolo"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 80,
            display: 'flex',
            flexDirection: 'column',
            background: 'rgba(26, 21, 53, 0.35)',
          }}
          onClick={() => setIndexOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              marginTop: 'auto',
              maxHeight: '72%',
              display: 'flex',
              flexDirection: 'column',
              background: tokens.bg,
              borderRadius: '22px 22px 0 0',
              boxShadow: '0 -16px 40px rgba(26,21,53,0.22)',
              paddingBottom: 'var(--sai-bottom)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 8px 10px 18px', borderBottom: `1px solid ${tokens.line}` }}>
              <span style={{ fontFamily: tokens.fonts.body, fontSize: 14, fontWeight: 800, color: tokens.text }}>
                Indice · {allSections.length} sezioni
              </span>
              <button
                type="button"
                onClick={() => setIndexOpen(false)}
                aria-label="Chiudi indice"
                style={{ width: 44, height: 44, border: 'none', background: 'transparent', cursor: 'pointer', color: tokens.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div style={{ overflowY: 'auto', padding: '8px 8px 14px' }}>
              {allSections.map((s) => (
                <button
                  key={s.num}
                  type="button"
                  onClick={() => jumpToSection(s.num)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    width: '100%',
                    minHeight: 44,
                    padding: '10px 10px',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 10,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ flexShrink: 0, fontFamily: tokens.fonts.mono, fontSize: 11, fontWeight: 700, color: tokens.textFaint, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {s.num}
                  </span>
                  <span style={{ flex: 1, fontFamily: tokens.fonts.body, fontSize: 13.5, fontWeight: 600, color: tokens.text, lineHeight: 1.35 }}>
                    {s.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <main
        ref={(el) => { mainRef.current = el; }}
        onScroll={handleScroll}
        className="asc-scroll"
        style={{
          flex:     1,
          overflow: 'auto',
          padding:  `16px 16px calc(${READING_DOCK_H}px + 84px + var(--sai-bottom, 0px))`,
        }}
      >
        {loading && (
          <div data-testid="teoria-skeleton" aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ height: 96, borderRadius: 14, background: tokens.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
            <div style={{ height: 18, width: '55%', borderRadius: 8, background: tokens.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
            <div style={{ height: 64, borderRadius: 12, background: tokens.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
            <div style={{ height: 64, borderRadius: 12, background: tokens.line, animation: 'asc-skel 1.4s ease-in-out infinite' }} />
          </div>
        )}
        {error && (
          <div style={statusStyle(tokens, 'wrong')}>{error}</div>
        )}

        {/* V29 S4 — sibling nav on TOP with names (founder-locked pattern). */}
        {!loading && chapter && (siblings.prev || siblings.next) && (
          <nav aria-label="Argomenti vicini" style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
            <SiblingButton dir="prev" sibling={siblings.prev} tokens={tokens} onGo={(id) => navigate(`/appendice/${id}/teoria`)} />
            <SiblingButton dir="next" sibling={siblings.next} tokens={tokens} onGo={(id) => navigate(`/appendice/${id}/teoria`)} />
          </nav>
        )}

        {!loading && chapter && (
          <>
            {/* 1. IN BREVE — the founder-reviewed Prof takeaways. */}
            {punti.length > 0 && (
              <section
                data-testid="teoria-in-breve"
                style={{
                  background:   tokens.accentSoft,
                  border:       `1px solid ${tokens.accent}`,
                  borderRadius: tokens.radius.lg,
                  padding:      '14px 16px',
                  marginBottom: 24,
                }}
              >
                <SectionHeading tokens={tokens}>In breve</SectionHeading>
                {chapter.prof_layer?.intro_template && (
                  <p
                    style={{
                      margin:     '0 0 10px',
                      fontFamily: tokens.fonts.body,
                      fontSize:   14,
                      lineHeight: 1.55,
                      color:      tokens.text,
                    }}
                  >
                    {chapter.prof_layer.intro_template}
                  </p>
                )}
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {punti.map((p) => (
                    <li key={p.principle_id} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      <span style={{ fontFamily: tokens.fonts.body, fontSize: 13.5, fontWeight: 700, color: tokens.text, lineHeight: 1.35 }}>
                        {p.name_it}
                      </span>
                      <span style={{ fontFamily: tokens.fonts.body, fontSize: 13, color: tokens.textMuted, lineHeight: 1.5 }}>
                        {p.takeaway}
                      </span>
                    </li>
                  ))}
                </ul>
                {/* V29 S4: the "Sintesi automatica — verifica sempre…" line was
                    internal provenance talk surfaced at students (audit note on
                    the approved before/after page) — removed; the per-rule
                    detail pages remain one tap away via Le regole chiave. */}
              </section>
            )}

            {/* 2. TIERED THEORY — core sub-sections expanded. The capitolo intro
                (Appendice-textbook campaign) opens the chapter as connected
                reference prose when the argomento has been rebuilt. */}
            {coreSections.length > 0 && (
              <section style={{ marginBottom: 24 }}>
                <SectionHeading tokens={tokens}>La teoria</SectionHeading>
                {chapter.capitolo_intro_md && (
                  <div style={{ marginBottom: 16 }}>
                    <ProfProse text={chapter.capitolo_intro_md} serif={false} size={14.5} />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {coreSections.map((s) => (
                    <SubSectionCard key={s.num} section={s} tokens={tokens} defaultOpen targetAnchor={targetAnchor} />
                  ))}
                </div>
                {chapter.capitolo_recap_md && (
                  <div style={{ marginTop: 16 }}>
                    <SectionHeading tokens={tokens}>Riepilogo</SectionHeading>
                    <ProfProse text={chapter.capitolo_recap_md} serif={false} size={14} />
                  </div>
                )}
              </section>
            )}

            {/* 3. LE REGOLE CHIAVE — principi as links into per-principle detail. */}
            {keyRules.length > 0 && argomentoId && (
              <section style={{ marginBottom: 24 }}>
                <SectionHeading tokens={tokens}>Le regole chiave</SectionHeading>
                <div data-testid="teoria-key-rules" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {keyRules.map((kr) => (
                    <KeyRuleRow
                      key={kr.principle_id}
                      rule={kr}
                      tokens={tokens}
                      onClick={() => navigate(`/appendice/${argomentoId}/${kr.principle_id}`)}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* 4. APPROFONDIMENTI — collapsed niche tail. */}
            {deepSections.length > 0 && (
              <section>
                <SectionHeading tokens={tokens}>Approfondimenti</SectionHeading>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {deepSections.map((s) => (
                    <SubSectionCard key={s.num} section={s} tokens={tokens} targetAnchor={targetAnchor} />
                  ))}
                </div>
              </section>
            )}

            {/* V29 S4 — quiz CTA: close the reading loop into the official
                questions of this argomento. */}
            {argomentoId && (coreSections.length > 0 || keyRules.length > 0) && (
              <button
                type="button"
                data-testid="teoria-quiz-cta"
                onClick={() => navigate(`/appendice/${argomentoId}/domande`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  minHeight: 48,
                  marginTop: 8,
                  border: 'none',
                  borderRadius: tokens.radius.md,
                  background: hue.grad,
                  color: '#fff',
                  fontFamily: tokens.fonts.body,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 3px 12px rgba(105,67,255,0.28)',
                }}
              >
                Mettiti alla prova · le domande d'esame
              </button>
            )}

            {coreSections.length === 0 && deepSections.length === 0 && keyRules.length === 0 && punti.length === 0 && (
              <div style={statusStyle(tokens, 'muted')}>
                Per questo argomento non c'è ancora una teoria da mostrare.
              </div>
            )}
          </>
        )}
      </main>

      {/* V29 S4 — reading dock: prev/next SIBLING argomento + Indice trigger.
          Mirrors the lesson dock so the two readers feel like one system. */}
      {!loading && chapter && (
        <nav
          data-testid="teoria-dock"
          aria-label="Navigazione teoria"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 45,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            height: `calc(${READING_DOCK_H}px + var(--sai-bottom, 0px))`,
            padding: `10px 14px calc(10px + var(--sai-bottom, 0px))`,
            background: tokens.mode === 'dark' ? 'rgba(22,16,31,0.9)' : 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            borderTop: `1px solid ${tokens.line}`,
          }}
        >
          <button
            type="button"
            aria-label={siblings.prev ? `Argomento precedente: ${siblings.prev.name_it}` : 'Nessun argomento precedente'}
            onClick={() => siblings.prev && navigate(`/appendice/${siblings.prev.id}/teoria`)}
            disabled={!siblings.prev}
            style={{
              width: 44, height: 44, borderRadius: 12,
              border: `1px solid ${tokens.lineStrong}`,
              background: tokens.surface,
              color: siblings.prev ? tokens.text : tokens.textFaint,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: siblings.prev ? 'pointer' : 'not-allowed',
              opacity: siblings.prev ? 1 : 0.5, flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M11 18l-6-6 6-6" /></svg>
          </button>
          <button
            type="button"
            data-testid="teoria-index-trigger"
            onClick={() => setIndexOpen(true)}
            aria-label="Apri l'indice del capitolo"
            style={{
              flex: 1,
              minHeight: 44,
              border: `1px solid ${tokens.lineStrong}`,
              borderRadius: 12,
              background: tokens.surface,
              color: tokens.text,
              fontFamily: tokens.fonts.body,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            Indice · {allSections.length} sezioni
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
          </button>
          <button
            type="button"
            aria-label={siblings.next ? `Argomento successivo: ${siblings.next.name_it}` : 'Nessun argomento successivo'}
            onClick={() => siblings.next && navigate(`/appendice/${siblings.next.id}/teoria`)}
            disabled={!siblings.next}
            style={{
              width: 44, height: 44, borderRadius: 12,
              border: 'none',
              background: siblings.next ? hue.grad : tokens.surface,
              color: siblings.next ? '#fff' : tokens.textFaint,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: siblings.next ? 'pointer' : 'not-allowed',
              opacity: siblings.next ? 1 : 0.5, flexShrink: 0,
              boxShadow: siblings.next ? '0 3px 12px rgba(105, 67, 255, 0.28)' : 'none',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </button>
        </nav>
      )}
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// SiblingButton — the top Precedente/Successivo pair with argomento NAMES
// (founder-locked V28 decision: prev/next relabelled with sibling names, on top).
// ---------------------------------------------------------------------------

function SiblingButton({
  dir,
  sibling,
  tokens,
  onGo,
}: {
  dir: 'prev' | 'next';
  sibling: ArgomentoDto | null;
  tokens: ReturnType<typeof useTokens>;
  onGo: (id: string) => void;
}): JSX.Element {
  const isNext = dir === 'next';
  return (
    <button
      type="button"
      data-testid={`teoria-sibling-${dir}`}
      onClick={() => sibling && onGo(sibling.id)}
      disabled={!sibling}
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 48,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        padding: '7px 12px',
        background: tokens.surface,
        border: `1px solid ${tokens.lineStrong}`,
        borderRadius: 12,
        cursor: sibling ? 'pointer' : 'default',
        textAlign: isNext ? 'right' : 'left',
        alignItems: isNext ? 'flex-end' : 'flex-start',
        opacity: sibling ? 1 : 0.45,
      }}
    >
      <span style={{ fontFamily: tokens.fonts.body, fontSize: 12.5, fontWeight: 800, color: tokens.accentInk }}>
        {isNext ? 'Successivo ›' : '‹ Precedente'}
      </span>
      <span
        style={{
          maxWidth: '100%',
          fontFamily: tokens.fonts.body,
          fontSize: 11,
          color: tokens.textMuted,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {sibling ? sibling.name_it : '—'}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// SubSectionCard — one tiered theory sub-section (frozen paragraphs +
// VERO/FALSO quiz readings). Collapsible; `approfondimenti` start collapsed.
// ---------------------------------------------------------------------------

function SubSectionCard({
  section,
  tokens,
  defaultOpen = false,
  targetAnchor = null,
}: {
  section: ChapterSubSection;
  tokens: ReturnType<typeof useTokens>;
  defaultOpen?: boolean;
  /** FP6 deep-link target (`anchorForSubsection('?sub')`); when it matches this
   *  card's anchor the card auto-expands, scrolls into view, and is highlighted. */
  targetAnchor?: string | null;
}): JSX.Element {
  const anchor = anchorForSubsection(section.num);
  const isTarget = targetAnchor !== null && anchor === targetAnchor;
  const [open, setOpen] = useState(defaultOpen || isTarget);
  // V29 S4: the chapter-index dropdown re-targets AFTER mount — a later
  // isTarget flip must reopen the card (useState above only sets the initial).
  useEffect(() => {
    if (isTarget) setOpen(true);
  }, [isTarget]);
  const text = contenutoToText(section.theory?.contenuto);
  const confermate = section.theory?.letture_confermate ?? [];
  const daScartare = section.theory?.letture_da_scartare ?? [];

  // FP6 exam-frequency hint chip (from the asset's exam_frequency count).
  const freq = section.exam_frequency ?? 0;
  const freqLabel = freq >= 8 ? 'Molto frequente' : freq >= 3 ? 'Frequente' : freq > 0 ? 'Saltuario' : null;

  // Auto-scroll into view when this sub-section is the deep-link target.
  useEffect(() => {
    if (!isTarget) return;
    const el = document.getElementById(anchor);
    // scrollIntoView is absent in jsdom (test env) — guard so the effect is a
    // no-op there instead of throwing.
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, [isTarget, anchor]);

  return (
    <div
      id={anchor}
      style={{
        scrollMarginTop: 12,
        background:   tokens.surface,
        border:       `1px solid ${isTarget ? tokens.accent : tokens.line}`,
        borderRadius: tokens.radius.md,
        overflow:     'hidden',
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width:          '100%',
          display:        'flex',
          alignItems:     'flex-start',
          gap:            10,
          background:     'transparent',
          border:         'none',
          padding:        '12px 14px',
          cursor:         'pointer',
          textAlign:      'left',
          minHeight:      44,
        }}
      >
        <span style={{ flexShrink: 0, marginTop: 2, fontFamily: tokens.fonts.mono, fontSize: 11, fontWeight: 700, color: tokens.textFaint, fontVariantNumeric: 'tabular-nums' }}>
          {section.num}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          {freqLabel && (
            <span style={{ display: 'inline-block', marginBottom: 4 }}>
              <span style={tagStyle(tokens)} title={`${freq} domande all'esame`}>
                {freqLabel}
              </span>
            </span>
          )}
          <span style={{ display: 'block', fontFamily: tokens.fonts.body, fontSize: 14.5, fontWeight: 700, color: tokens.text, lineHeight: 1.3 }}>
            {section.title}
          </span>
        </span>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke={tokens.textFaint} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, marginTop: 2, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.16s ease' }}
          aria-hidden="true"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>

      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          {/* Appendice-textbook campaign: gated reference prose renders FIRST;
              the legacy summary bullets are suppressed when it exists (the prose
              supersedes them — quiz-check lists below stay). */}
          {section.theory?.capitolo_md
            ? <ProfProse text={section.theory.capitolo_md} serif={false} size={14.5} />
            : (text && <ProfProse text={text} serif={false} size={14} />)}
          <SubSectionImages
            images={section.images ?? []}
            title={section.title}
            tokens={tokens}
          />

          {/* ✅ VERO nei quiz */}
          {confermate.length > 0 && (
            <QuizReadingList
              kind="vero"
              label="Vero nei quiz"
              items={confermate}
              tokens={tokens}
            />
          )}

          {/* ❌ FALSO nei quiz — spec §0.2 H3: framed unmistakably as FALSE,
              never as an affirmative statement. */}
          {daScartare.length > 0 && (
            <QuizReadingList
              kind="falso"
              label="Falso nei quiz"
              items={daScartare}
              tokens={tokens}
            />
          )}

          {/* FP6 source attribution (when the asset provides it). */}
          {section.theory?.source_description && (
            <div style={{ marginTop: 10, fontFamily: tokens.fonts.mono, fontSize: 10, color: tokens.textFaint }}>
              Fonte: {section.theory.source_description}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function appendiceImageRef(image: ChapterSubSectionImage): string | null {
  const raw = typeof image.path === 'string' && image.path.trim()
    ? image.path
    : typeof image.ref === 'string' && image.ref.trim()
      ? image.ref
      : '';
  return raw ? raw.trim() : null;
}

function SubSectionImages({
  images,
  title,
  tokens,
}: {
  images: ChapterSubSectionImage[];
  title: string;
  tokens: ReturnType<typeof useTokens>;
}): JSX.Element | null {
  const clean = images
    .map((image) => ({ image, ref: appendiceImageRef(image) }))
    .filter((item): item is { image: ChapterSubSectionImage; ref: string } => Boolean(item.ref));
  if (clean.length === 0) return null;

  return (
    <div
      data-testid="appendice-subsection-images"
      style={{
        display: 'grid',
        gridTemplateColumns: clean.length === 1 ? 'minmax(0, 220px)' : 'repeat(auto-fit, minmax(108px, 1fr))',
        gap: 10,
        alignItems: 'stretch',
        justifyContent: 'center',
        margin: '12px 0 4px',
      }}
    >
      {clean.map(({ image, ref }, i) => {
        const name = image.description?.italianName;
        const alt = name ?? (clean.length > 1 ? `Immagine ${i + 1} di ${clean.length}: ${title}` : `Immagine: ${title}`);
        return (
          <span
            key={`${ref}-${i}`}
            data-appendice-image-frame
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 118,
              background: tokens.surface2,
              border: `1px solid ${tokens.line}`,
              borderRadius: tokens.radius.md,
              padding: 10,
              overflow: 'hidden',
            }}
          >
            <img
              src={lessonAssetSrc(ref)}
              alt={alt}
              loading="lazy"
              decoding="async"
              style={{
                display: 'block',
                width: '100%',
                height: 140,
                maxWidth: '100%',
                maxHeight: 180,
                objectFit: 'contain',
              }}
              onError={(event) => {
                const frame = event.currentTarget.closest('[data-appendice-image-frame]');
                if (frame instanceof HTMLElement) frame.style.display = 'none';
              }}
            />
            <SelectProfFigureButton selection={{
              reference: { kind: 'asset', imageRef: lessonAssetSrc(ref).slice(1) },
              imageRef: lessonAssetSrc(ref).slice(1), label: alt,
            }} />
          </span>
        );
      })}
    </div>
  );
}

// FP6 exam-frequency hint chip (accent-tinted, mono uppercase).
function tagStyle(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return {
    background:    tokens.accentSoft,
    color:         tokens.accentInk,
    fontFamily:    tokens.fonts.mono,
    fontSize:      9.5,
    fontWeight:    600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    padding:       '2px 7px',
    borderRadius:  tokens.radius.pill,
  };
}

// ---------------------------------------------------------------------------
// QuizReadingList — a ✅ VERO or ❌ FALSO list of quiz statements. The FALSO
// list is visually distinct (rose icon + rose label) and carries the explicit
// "Falso nei quiz" framing so a da_scartare item is NEVER read as true.
// ---------------------------------------------------------------------------

function QuizReadingList({
  kind,
  label,
  items,
  tokens,
}: {
  kind: 'vero' | 'falso';
  label: string;
  items: string[];
  tokens: ReturnType<typeof useTokens>;
}): JSX.Element {
  const isVero = kind === 'vero';
  const accent = isVero ? tokens.rightText : tokens.wrongText;
  const soft = isVero ? tokens.rightSoft : tokens.wrongSoft;
  return (
    <div
      data-testid={isVero ? 'letture-confermate' : 'letture-da-scartare'}
      style={{
        marginTop:    14,
        background:    soft,
        borderRadius: tokens.radius.md,
        padding:      '10px 12px',
      }}
    >
      <div
        style={{
          display:       'inline-flex',
          alignItems:    'center',
          gap:           6,
          marginBottom:  8,
          color:         accent,
          fontFamily:    tokens.fonts.mono,
          fontSize:      11,
          fontWeight:    700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        {isVero ? <CheckIcon /> : <CrossIcon />}
        {label}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
            <span style={{ flexShrink: 0, marginTop: 2, color: accent, display: 'inline-flex' }} aria-hidden="true">
              {isVero ? <CheckIcon /> : <CrossIcon />}
            </span>
            <span style={{ fontFamily: tokens.fonts.body, fontSize: 13.5, color: tokens.text, lineHeight: 1.5, minWidth: 0 }}>
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KeyRuleRow — one principle (key_rule) as a tappable row into its detail.
// ---------------------------------------------------------------------------

function KeyRuleRow({
  rule,
  tokens,
  onClick,
}: {
  rule: ChapterKeyRule;
  tokens: ReturnType<typeof useTokens>;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        width:        '100%',
        background:   tokens.surface,
        border:       `1px solid ${tokens.line}`,
        borderRadius: tokens.radius.md,
        padding:      '10px 14px',
        minHeight:    44,
        cursor:       'pointer',
        textAlign:    'left',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily:   tokens.fonts.body,
            fontSize:     13.5,
            fontWeight:   600,
            color:        tokens.text,
            lineHeight:   1.35,
          }}
        >
          {rule.name_it}
        </div>
      </div>
      <svg
        width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke={tokens.textFaint} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
        style={{ flexShrink: 0 }}
        aria-hidden="true"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Small shared bits
// ---------------------------------------------------------------------------

function SectionHeading({
  tokens,
  children,
}: {
  tokens: ReturnType<typeof useTokens>;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        fontFamily:    tokens.fonts.mono,
        fontSize:      11,
        fontWeight:    600,
        color:         tokens.textFaint,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        margin:        '0 2px 10px',
      }}
    >
      {children}
    </div>
  );
}

function statusStyle(
  tokens: ReturnType<typeof useTokens>,
  variant: 'muted' | 'wrong',
): React.CSSProperties {
  return {
    fontFamily: tokens.fonts.body,
    fontSize:   variant === 'wrong' ? 13 : 14,
    color:      variant === 'wrong' ? tokens.wrong : tokens.textMuted,
    padding:    variant === 'wrong' ? '12px 0' : '24px 0',
    textAlign:  'center',
  };
}

function CheckIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" width={12} height={12} aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CrossIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" width={12} height={12} aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
